(() => {
  'use strict';
  if (window.FenloNativeCommerce) return;
  const ROOT = '.fenlo-native-page[data-fenlo-native]';
  const BOX = '[data-pf-type="ProductBox"],[data-pf-type="ProductBox2"]';
  const ATC = '[data-pf-type="ProductATC"],[data-pf-type="ProductATC2"]';
  const SWATCH = '[data-pf-type="ProductVariantSwatches"]';
  const states = new WeakMap();
  const labels = new WeakMap();
  const mounted = new WeakSet();
  const rootPath = () => window.Shopify?.routes?.root || '/';
  const emit = (node, name, detail) => node.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  const isProductForm = form => form instanceof HTMLFormElement && !!form.closest(ROOT) && /\/cart\/add(?:\.js)?\/?$/.test(new URL(form.action, location.href).pathname);
  const productID = form => form.dataset.productid || form.dataset.productId || form.closest(BOX)?.dataset.productId;
  const scope = form => form.closest(BOX) || form;
  const local = (form, selector) => [...scope(form).querySelectorAll(selector)].filter(el => !el.closest('form') || el.closest('form') === form);

  function productFor(form) {
    const script = form.querySelector('script[data-fenlo-product]');
    if (script) {
      try { const p = JSON.parse(script.textContent); if (Array.isArray(p?.variants)) return p; } catch (_) {}
    }
    const id = productID(form);
    const direct = window.fenloNativeProducts?.[id];
    if (Array.isArray(direct?.variants)) return direct;
    for (const collection of Object.values(window.fenloNativeCollections || {})) {
      const found = collection.products?.find(p => String(p.id) === String(id));
      if (found) return found;
    }
    return null;
  }

  function selectedVariant(form, product, changed) {
    const swatch = local(form, SWATCH)[0];
    if (swatch) {
      const combined = swatch.dataset.combined === 'true' || swatch.querySelector('[data-option-name="PFCombined"]');
      if (combined) {
        const control = changed || swatch.querySelector('select') || swatch.querySelector('input[type="radio"]:checked');
        if (control?.value) return product.variants.find(v => String(v.id) === control.value) || null;
      } else {
        const options = [...swatch.querySelectorAll('select[data-option-name],input[type="radio"]:checked')];
        if (options.length) {
          const values = product.options.map((option, index) => {
            const name = typeof option === 'string' ? option : option.name;
            return options.find(el => (el.dataset.optionName || el.closest('[data-option-name]')?.dataset.optionName) === name)?.value
              ?? options.find(el => Number(el.closest('[data-unique-id]')?.dataset.uniqueId) === index + 1)?.value;
          });
          return product.variants.find(v => values.every((value, i) => value !== undefined && String(v.options?.[i] ?? v['option' + (i + 1)]) === value)) || null;
        }
      }
    }
    const id = form.querySelector('[name="id"]')?.value;
    return product.variants.find(v => String(v.id) === id) || product.variants.find(v => v.available) || product.variants[0] || null;
  }

  function label(button, text) {
    if (!labels.has(button)) labels.set(button, button.innerHTML);
    const child = button.querySelector('[data-pf-type="Text"]');
    if (text === null) button.innerHTML = labels.get(button);
    else if (child) child.textContent = text;
    else button.textContent = text;
  }

  function notice(form, text, error = false, cartLink = false) {
    let el = form.querySelector('[data-fenlo-cart-message]');
    if (!el) {
      el = document.createElement('p');
      el.dataset.fenloCartMessage = '';
      el.setAttribute('aria-live', 'polite');
      form.appendChild(el);
    }
    el.setAttribute('role', error ? 'alert' : 'status');
    el.textContent = text;
    el.hidden = !text;
    if (cartLink) {
      const link = document.createElement('a');
      link.href = rootPath() + 'cart';
      link.textContent = ' View cart';
      el.appendChild(link);
    }
  }

  function money(cents) {
    const format = window.MinimogSettings?.money_format || window.Shopify?.money_format || window.fenloNativeSettings?.moneyFormat;
    if (window.Shopify?.formatMoney && format) {
      try {
        const html = window.Shopify.formatMoney(cents, format);
        return new DOMParser().parseFromString(String(html), 'text/html').body.textContent;
      } catch (_) {}
    }
    const currency = window.Shopify?.currency?.active || window.MinimogSettings?.currency_code;
    return new Intl.NumberFormat(document.documentElement.lang || 'en', currency ? { style: 'currency', currency } : { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(cents) / 100);
  }

  function clampQuantity(input, value) {
    const min = Math.max(1, Number(input.min) || 1);
    const max = input.max !== '' && Number.isFinite(Number(input.max)) ? Math.max(min, Number(input.max)) : Infinity;
    const step = Math.max(1, Number(input.step) || 1);
    const parsed = Number(value);
    const n = Number.isFinite(parsed) ? parsed : min;
    input.value = String(Math.min(max, Math.max(min, min + Math.floor((n - min) / step) * step)));
    return Number(input.value);
  }

  function sync(form, changed) {
    const state = states.get(form);
    if (!state?.product) return;
    const previous = state.variant;
    const variant = selectedVariant(form, state.product, changed);
    state.variant = variant;
    let ids = [...form.querySelectorAll('[name="id"]')];
    if (!ids.length) {
      const id = document.createElement('input');
      id.type = 'hidden'; id.name = 'id'; form.appendChild(id); ids = [id];
    }
    ids.forEach(id => { id.value = variant ? String(variant.id) : ''; });
    local(form, ATC).forEach(button => {
      const available = !!variant?.available;
      button.disabled = !available || state.busy;
      button.setAttribute('aria-disabled', String(button.disabled));
      if (!state.busy) label(button, available ? null : (button.dataset.soldout || window.MinimogStrings?.soldOut || 'Sold out'));
    });
    local(form, '[data-product-type="price"],[data-product-type="compare_at_price"]').forEach(el => {
      const compare = el.dataset.productType === 'compare_at_price';
      const value = compare ? variant?.compare_at_price : variant?.price;
      el.hidden = compare && !(Number(value) > Number(variant?.price));
      if (variant && value != null) el.textContent = money(value);
    });
    local(form, '[data-pf-type="QuantityField"],input[name="quantity"]').forEach(input => {
      if (!input.dataset.fenloInitialMax) input.dataset.fenloInitialMax = input.max || 'none';
      const continued = variant?.inventory_policy === 'continue' || (input.dataset.variantsContinue || '').split(/\s+/).includes(String(variant?.id));
      if (continued || (previous && previous.id !== variant?.id && !Number.isFinite(variant?.inventory_quantity))) input.removeAttribute('max');
      else if (Number.isFinite(variant?.inventory_quantity) && variant.inventory_management) input.max = String(Math.max(1, variant.inventory_quantity));
      clampQuantity(input, input.value);
    });
    const swatch = local(form, SWATCH)[0];
    if (swatch?.dataset.combined === 'true') {
      swatch.querySelectorAll('select option,input[type="radio"]').forEach(el => {
        const option = state.product.variants.find(v => String(v.id) === el.value);
        if (option) el.disabled = !option.available;
      });
    }
    if (variant && previous?.id !== variant.id) {
      const media = variant.featured_media || variant.featured_image;
      // Liquid and the gallery retain the original initial featured media.
      if (media && (previous || changed)) emit(scope(form), 'fenlo:media-select', { mediaId: media.id, src: media.preview_image?.src || media.src });
      emit(scope(form), 'fenlo:commerce-variant', { productId: state.product.id, variant });
    }
  }

  function initializeForm(form) {
    if (!isProductForm(form) || states.has(form)) return;
    const product = productFor(form);
    states.set(form, { product, variant: null, busy: false });
    local(form, ATC).forEach(button => labels.set(button, button.innerHTML));
    if (!product) {
      local(form, ATC).forEach(button => { button.disabled = true; });
      notice(form, 'Product options could not be loaded. Please open the product page.', true);
      return;
    }
    sync(form);
  }

  async function add(form, button) {
    initializeForm(form);
    const state = states.get(form);
    if (!state || state.busy || !state.variant?.available) return;
    sync(form);
    if (!state.variant?.available || !form.reportValidity()) return;
    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = quantityInput ? clampQuantity(quantityInput, quantityInput.value) : 1;
    const body = new FormData(form);
    body.set('id', String(state.variant.id));
    body.set('quantity', String(quantity));
    state.busy = true;
    local(form, ATC).forEach(el => { el.disabled = true; el.setAttribute('aria-busy', 'true'); label(el, el.dataset.adding || 'Adding...'); });
    notice(form, '');
    let added = false;
    try {
      const response = await fetch(rootPath() + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
      const data = await response.json();
      if (!response.ok || data.status) throw Object.assign(new Error(data.description || data.message || 'This item could not be added.'), { confirmedFailure: true });
      added = true;
      const item = data.items?.[0] || data;
      notice(form, button?.dataset.added || window.MinimogStrings?.itemAdded || 'Added to cart.', false, true);
      emit(scope(form), 'fenlo:cart-added', { productId: state.product.id, variantId: state.variant.id, quantity, item });
      if (button?.dataset.checkout === 'checkout') location.assign(rootPath() + 'checkout');
      else if (window.MinimogTheme?.Cart && typeof window.Shopify?.onItemAdded === 'function') await window.Shopify.onItemAdded(item, !!window.MinimogSettings?.enable_cart_drawer);
      else if (window.MinimogTheme?.Cart?.refreshCart) {
        const cart = window.MinimogTheme.Cart;
        await cart.refreshCart();
        if (window.MinimogSettings?.enable_cart_drawer) { await cart.renderNewCart(); cart.openCartDrawer(); }
      } else location.assign(rootPath() + 'cart');
    } catch (error) {
      if (added) notice(form, 'Added to cart. Open your cart to continue.', false, true);
      else if (error.confirmedFailure) notice(form, error.message, true);
      else notice(form, 'We could not confirm whether this item was added. Check your cart before trying again.', true, true);
    } finally {
      state.busy = false;
      local(form, ATC).forEach(el => el.removeAttribute('aria-busy'));
      sync(form);
    }
  }

  function settingsFor(el) {
    const root = el.closest(ROOT);
    let settings = window.fenloNativeSettings || {};
    const holder = el.closest('[data-fenlo-settings]');
    if (holder) {
      try { settings = JSON.parse(holder.dataset.fenloSettings); } catch (_) {}
    }
    const sectionSettings = settings[root?.dataset.fenloNative] || settings;
    const data = sectionSettings.elementData || sectionSettings;
    for (const c of el.classList) if (data[c]) return data[c];
    return {};
  }

  function linkFor(el) {
    if (el.closest('a,button,input,select,textarea,[role="button"]')) return null;
    const config = settingsFor(el);
    if (config.clickAction === 0 && !/ProductTitle/.test(el.dataset.pfType || '')) return null;
    const url = el.dataset.href;
    if (!url) return null;
    try { const target = new URL(url, location.href); return /^https?:$/.test(target.protocol) ? target : null; } catch (_) { return null; }
  }

  function init(container = document) {
    const roots = container.matches?.(ROOT) ? [container] : [...container.querySelectorAll(ROOT)];
    roots.forEach(root => {
      root.querySelectorAll('form').forEach(initializeForm);
      root.querySelectorAll('[data-href]').forEach(el => {
        if (linkFor(el)) { el.setAttribute('role', 'link'); if (!el.hasAttribute('tabindex')) el.tabIndex = 0; }
      });
      if (mounted.has(root)) return;
      mounted.add(root);
      let pointer = null;
      let dragged = false;
      root.addEventListener('pointerdown', e => { pointer = { x: e.clientX, y: e.clientY }; dragged = false; }, { passive: true });
      root.addEventListener('pointermove', e => { if (pointer && Math.hypot(e.clientX - pointer.x, e.clientY - pointer.y) > 6) dragged = true; }, { passive: true });
      root.addEventListener('pointerup', () => { pointer = null; }, { passive: true });
      root.addEventListener('click', e => {
        const target = e.target.closest?.(ATC);
        if (target) {
          const form = target.form || target.closest('form');
          if (!isProductForm(form)) return;
          e.preventDefault(); e.stopImmediatePropagation();
          if (!target.disabled) add(form, target);
          return;
        }
        const quantity = e.target.closest?.('[data-quantity-action]');
        if (quantity) {
          const input = quantity.closest('[data-pf-type="ProductQuantity"]')?.querySelector('input');
          if (!input) return;
          e.preventDefault(); e.stopImmediatePropagation();
          clampQuantity(input, Number(input.value) + (quantity.dataset.quantityAction === 'decrease' ? -1 : 1) * (Number(input.step) || 1));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        const link = e.target.closest?.('[data-href]');
        if (!link || e.defaultPrevented || dragged || e.target.closest('button,a,input,select,textarea,[role="button"]')) return;
        const url = linkFor(link);
        if (!url || e.button !== 0) return;
        e.preventDefault();
        if (e.metaKey || e.ctrlKey || e.shiftKey) window.open(url.href, '_blank', 'noopener');
        else location.assign(url.href);
      }, true);
      root.addEventListener('keydown', e => {
        if (e.key !== 'Enter') return;
        const el = e.target.closest?.('[data-href][role="link"]');
        const url = el && linkFor(el);
        if (url && e.target === el) { e.preventDefault(); location.assign(url.href); }
      });
      root.addEventListener('change', e => {
        const form = e.target.form || e.target.closest('form');
        if (!isProductForm(form)) return;
        initializeForm(form);
        if (e.target.closest(SWATCH)) sync(form, e.target);
        if (e.target.matches('[name="quantity"]')) clampQuantity(e.target, e.target.value);
      });
      root.addEventListener('submit', e => {
        if (!isProductForm(e.target)) return;
        if (e.submitter?.closest('.shopify-payment-button') || e.submitter?.name === 'checkout') return;
        e.preventDefault(); e.stopImmediatePropagation();
        add(e.target, e.submitter || e.target.querySelector(ATC));
      }, true);
    });
  }

  window.FenloNativeCommerce = Object.freeze({ init });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('fenlo:content-ready', e => init(e.target));
})();
